import { useEffect, useState } from 'react';

import { cssModules } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import {
  fetchCultureSelectorData,
  useCultureSelectorData,
} from '@web-platform/global-components-culture-selector/helpers';
import GlobalHeader from '@web-platform/global-components-header';
import { loginModalInit } from '@web-platform/global-components-header/helpers';
import { SubscriptionClient } from '@web-platform/global-components-login-form/helpers';
import { isBrowser } from '@web-platform/shared-travel-tools-utils-frontend';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';
import { getProfilePicture, isLoggedIn } from 'saddlebag-user-management';

import { loadClient } from './loadClient';

import type { SubscriptionClientInterface } from '@web-platform/global-components-google-one-tap';
import type { PartialAuthClient } from '@web-platform/global-components-login-form/types/auth-client';
import type { HeaderProps } from '@web-platform/shared-travel-tools-types/src/header-props';
import type { PageContext } from '@web-platform/shared-travel-tools-types/src/page-context';

import STYLES from './Header.module.scss';

const cls = cssModules(STYLES);

type Props = HeaderProps & {
  pageContext: PageContext;
  isProductionEnv: boolean;
};
export const Header = ({
  cultureSelectorProps,
  isProductionEnv,
  locale,
  market,
  pageContext,
  v11__LoginFormProps,
  ...rest
}: Props) => {
  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(fetchCultureSelectorData, locale, false);
  const [isLoginFormOpen, setIsLoginFormOpen] = useState(false);
  const [authClient, setAuthClient] = useState<PartialAuthClient | undefined>(undefined);
  const [subscriptionClient] = useState<SubscriptionClientInterface | undefined>(() =>
    typeof window === 'undefined' ? undefined : new SubscriptionClient(),
  );
  useEffect(() => {
    loginModalInit({ setLoginFormVisible: setIsLoginFormOpen }).catch((error: Error) => {
      logError(error, { component: 'Header', action: 'loginModalInit' });
    });
  }, []);
  useEffect(() => {
    if (!authClient) {
      loadClient('AuthClient')
        .then((AuthClient) => {
          setAuthClient(
            new AuthClient({
              authConsumer: 'travel-tools',
              env: isProductionEnv ? 'production' : 'development',
              locale,
              market,
              strings: v11__LoginFormProps.authClient.strings,
            }),
          );
          return undefined;
        })
        .catch((error: Error) => {
          logError(error, { component: 'Header', action: 'loadAuthClient' });
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const headerProps = {
    ...rest,
    market,
    locale,
    cultureSelectorProps: {
      ...cultureSelectorProps,
      markets,
      locales,
      currencies,
      enMarkets,
      isProductionEnv,
    },
  };
  return (
    <div
      id="global-header-container"
      data-testid="global-header"
      data-page-type={pageContext.pageType}
      className={cls('headerWrapper')}
    >
      <GlobalHeader
        {...headerProps}
        isFetchingCultureSelectorData={isFetchingCultureSelectorData}
        onCultureSelectorButtonClick={onCultureSelectorButtonClick}
        logger={{ logOperationalEvent, logError, logWarn }}
        userLoggedIn={isBrowser() ? isLoggedIn() : Promise.resolve(pageContext.isLoggedIn)}
        getProfilePicture={getProfilePicture}
        isProductionEnv={isProductionEnv}
        loginFormProps={
          authClient && subscriptionClient
            ? {
                ...v11__LoginFormProps,
                consumer: 'travel-tools',
                isProductionEnv,
                authClient,
                isOpen: isLoginFormOpen,
                screenName: pageContext.pageType,
                subscriptionClient,
                onVisibilityChange: ({ shouldBeOpen }: { shouldBeOpen?: boolean }) => {
                  setIsLoginFormOpen(!!shouldBeOpen);
                },
              }
            : undefined
        }
      />
    </div>
  );
};
